
import { Options, Vue } from "vue-class-component";
import MButton from "@/components/MButton.vue";
import MIcon from "@/components/MIcon.vue";
import MTable from "@/components/MTable.vue";
import MNavBar from "../components/MNavBar.vue";
import MHeaderPanel from "@/components/MHeaderPanel.vue";
import MsAddModal from "@/components/staff/MsAddModal.vue";
import MsEditModal from "@/components/staff/MsEditModal.vue";
import MsCsvUpdateModal from "@/components/staff/MsCsvUpdateModal.vue";
import { Classroom } from "@/entities/classroom";
import { School, SchoolConfig } from "@/entities/school";
import { Domains } from "@/env";
import {
  getTutorDetailsOfSchools,
  TutorMeta,
  bulkUpdateTutors,
  BulkUpdateTutorData
} from "@/api/tutor";
import { Getter, State } from "@/store/helper";
import { convertToTutorTableData, tutorsTableDefinition } from "@/utils/table";
import { User } from "@/entities/user";
import { getLoginTutorsOfSchool } from "@/api/user";
import { getClassRoomsBasedOnRole } from "@/api/classroom";
import store from "@/store/index";
import { escapeCsvField, exportToUTF8Csv, readAsText } from "@/utils/csv";
import {
  getAuthorityMap,
  getAuthorityReverseMap,
  validateTutorName,
  validateAuthorityDisplayName,
  validateClassroomNames,
  checkIsAdminOrOwnerOfSchoolFromAuthority
} from "@/utils/tutor";
import { validEmail } from "@/utils/email";
import { TutorAuthority } from "@/entities/tutor";
import dayjs from "dayjs";
import parse from "csv-parse/lib/sync";
import { parseClassroomNames } from "@/utils/classroom";
import {
  formatPasswordLoginDisabledCell,
  PASSWORD_LOGIN_DISABLED_HEADER,
  readPasswordLoginCell
} from "@/utils/passwordLoginCsv";

@Options({
  components: {
    MButton,
    MIcon,
    MTable,
    MNavBar,
    MHeaderPanel,
    MsAddModal,
    MsEditModal,
    MsCsvUpdateModal
  },
  watch: {
    accessibleClassRooms: function() {
      this.updateDatas();
    },
    tutors: function() {
      this.updateDatas();
    }
  }
})
export default class StaffList extends Vue {
  datas: { [key: string]: string | number | boolean }[] = [];
  definitions = tutorsTableDefinition;
  searchKeyword = "";
  selectedId = "";
  isDisplayedAddTutorModal = false;
  isDisplayedEditTutorModal = false;
  isDisplayedCsvUpdateModal = false;
  tutorUsers: User[] = [];

  @State("classrooms") classrooms!: Classroom[];
  @State("school") school?: School;
  @State("schoolConfig") schoolConfig!: SchoolConfig | null;
  @State("tutor") tutor!: TutorMeta | null;
  @State("tutors") tutors!: TutorMeta[];
  @Getter("isAdmin") isAdmin!: boolean;
  @Getter("isAdminOfSchool") isAdminOfSchool!: boolean;
  @Getter("isSupervisorOfSchool") isSupervisorOfSchool!: boolean;
  @Getter("isServiceProvider") isServicePrivider!: boolean;

  get isStudyRoomOnly(): boolean {
    return this.schoolConfig?.data.useStudyRoomOnly ?? false;
  }

  get newStaffManagementUrl(): string {
    const domains = new Domains({ usePrefix: true });
    return `${domains.domains.schoolAi}/staff_management`;
  }

  get isSchool(): boolean {
    return store.state.isSchool;
  }

  get isOwnerOfSchool(): boolean {
    return this.tutor?.config.data.authority === "owner";
  }

  get canBulkUpdateTutors(): boolean {
    if (!this.tutor) return false;
    // オーナーは常時OK
    if (this.isOwnerOfSchool) return true;
    // admin && allowBulkUpdateTutors
    if (
      this.tutor.config.data.authority === "admin" &&
      this.tutor.main.data.allowBulkUpdateTutors
    ) {
      return true;
    }
    return false;
  }

  get accessibleClassRooms(): Classroom[] {
    if (this.isAdmin) {
      return [];
    }
    const isServicePrivider = this.isServicePrivider ?? false;
    const schoolDocId = this.school?.ref.id;
    if (!schoolDocId) {
      return [];
    }
    const classrooms = getClassRoomsBasedOnRole(
      isServicePrivider,
      this.classrooms,
      schoolDocId!
    );
    return classrooms;
  }

  get selectedTutor(): TutorMeta | null {
    if (!this.selectedId) {
      return null;
    }
    const matchTutors = this.tutors.filter(
      tutor => tutor.main.ref.id === this.selectedId
    );
    if (matchTutors.length === 0) {
      return null;
    }

    return matchTutors[0];
  }

  get selectedTutorEmail() {
    return (
      this.tutorUsers.find(user => user.uid === this.selectedId)?.email ?? ""
    );
  }

  get filteredDatas(): { [key: string]: string | number | boolean }[] {
    if (this.searchKeyword.length === 0) {
      return this.datas;
    }

    return this.datas.filter(data => {
      return (
        (Object.keys(data).includes("name") &&
          data.name
            .toString()
            .toLowerCase()
            .includes(this.searchKeyword.toLowerCase())) ||
        (Object.keys(data).includes("suf-name") &&
          data["suf-name"]
            .toString()
            .toLowerCase()
            .includes(this.searchKeyword.toLowerCase()))
      );
    });
  }

  showAddModal() {
    this.isDisplayedAddTutorModal = true;
  }

  closeAddModal() {
    this.isDisplayedAddTutorModal = false;
  }

  showEditModal() {
    if (!this.selectedTutor || !this.tutor) {
      return;
    }
    if (this.selectedTutor.config.data.authority === "owner") {
      alert(
        `${
          this.isSchool ? "管理職" : "オーナー"
        }権限を持つスタッフを編集することはできません`
      );
      return;
    } else if (this.selectedTutor.config.data.authority === "admin") {
      if (this.tutor.config.data.authority !== "owner") {
        alert(
          `${
            this.isSchool ? "ICT担当者" : "管理者"
          }権限を持つスタッフを編集することはできません`
        );
        return;
      }
    } else if (this.selectedTutor.config.data.authority === "supervisor") {
      if (!["owner", "admin"].includes(this.tutor.config.data.authority)) {
        alert("責任者権限を持つスタッフを編集することはできません");
        return;
      }
    } else if (this.tutor.config.data.authority === "general") {
      alert("スタッフを編集する権限を持っていません");
      return;
    }
    this.isDisplayedEditTutorModal = true;
  }

  closeEditModal() {
    this.isDisplayedEditTutorModal = false;
  }

  changeSelectedId(id: string) {
    this.selectedId = id;
    this.showEditModal();
  }

  created() {
    this.updateDatas();
  }

  async updateDatas() {
    if (this.tutors.length === 0) {
      return;
    }

    this.datas = this.tutors.map(tutor => {
      return convertToTutorTableData(tutor, this.accessibleClassRooms, {
        isSchool: store.state.isSchool
      });
    });

    const schoolDocId = this.school?.ref.id;
    if (!schoolDocId) {
      return;
    }

    this.tutorUsers = await getLoginTutorsOfSchool(schoolDocId);
  }

  // まとめて編集のハンドラー
  handleEditStaffViaCsv() {
    this.isDisplayedCsvUpdateModal = true;
  }

  closeCsvUpdateModal() {
    this.isDisplayedCsvUpdateModal = false;
  }

  handleClickFileInputRef() {
    if (
      !this.$refs.fileInput ||
      !(this.$refs.fileInput instanceof HTMLInputElement)
    ) {
      alert("問題が発生しました。時間をおいて再度実行してください。");
      return;
    }
    this.$refs.fileInput.click();
  }

  // CSVから読み込んだスタッフデータの型
  parsedStaffData: BulkUpdateTutorData[] = [];

  validateAndConvertStaffRecords(
    records: { [key: string]: string }[]
  ): BulkUpdateTutorData[] {
    const authorityReverseMap = getAuthorityReverseMap({
      isSchool: this.isSchool
    });
    const authorityMap = getAuthorityMap({ isSchool: this.isSchool });
    const validAuthorities = Object.keys(authorityReverseMap);
    const validClassroomNamesList = this.accessibleClassRooms.map(
      c => c.data.name
    );
    const existingTutorIds = this.tutors.map(t => t.main.ref.id);

    // 現在のオーナーを取得
    const currentOwner = this.tutors.find(
      t => t.config.data.authority === "owner"
    );
    const currentOwnerId = currentOwner?.main.ref.id;

    const processedIds: string[] = [];
    const processedGmails: string[] = [];
    const processedMicrosoftEmails: string[] = [];
    const processedEmails: string[] = [];

    // 1回目のパス: 全レコードをパースしてオーナー数をチェック
    const parsedRecords: {
      rowNum: number;
      id: string;
      name: string;
      authority: TutorAuthority;
      gmail: string;
      microsoftEmail: string;
      email: string;
      classroomNames: string[];
      passwordLoginDisabled?: boolean;
    }[] = [];

    const passwordLoginKey = Object.keys(records[0] ?? {}).find(
      key => key.trim() === PASSWORD_LOGIN_DISABLED_HEADER
    );

    for (let index = 0; index < records.length; index++) {
      const record = records[index];
      const rowNum = index + 2; // ヘッダー行を考慮

      // IDの取得（ヘッダーが "ID *編集不可" なので対応）
      const idKey = Object.keys(record).find(key => key.startsWith("ID"));
      if (!idKey) {
        throw new Error(`${rowNum}行目: IDカラムが見つかりません。`);
      }
      const id = String(record[idKey] ?? "").trim();
      if (!id) {
        throw new Error(`${rowNum}行目: IDが空です。`);
      }
      if (!existingTutorIds.includes(id)) {
        throw new Error(`${rowNum}行目: ID「${id}」のスタッフが存在しません。`);
      }
      if (processedIds.includes(id)) {
        throw new Error(`${rowNum}行目: ID「${id}」が重複しています。`);
      }
      processedIds.push(id);

      // 名前
      const nameKey = Object.keys(record).find(key => key === "名前");
      const name = String(record[nameKey ?? ""] ?? "").trim();
      if (!validateTutorName(name)) {
        throw new Error(`${rowNum}行目: 名前が空です。`);
      }

      // 権限
      const authorityKey = Object.keys(record).find(key => key === "権限");
      const authorityDisplayName = String(
        record[authorityKey ?? ""] ?? ""
      ).trim();
      if (
        !validateAuthorityDisplayName(authorityDisplayName, validAuthorities)
      ) {
        throw new Error(
          `${rowNum}行目: 権限「${authorityDisplayName}」は無効です。有効な値: ${validAuthorities.join(
            ", "
          )}`
        );
      }
      const authority = authorityReverseMap[authorityDisplayName];

      // Googleアカウント
      const gmailKey = Object.keys(record).find(
        key => key === "Googleアカウント"
      );
      const gmail = String(record[gmailKey ?? ""] ?? "").trim();
      if (gmail && !validEmail(gmail)) {
        throw new Error(
          `${rowNum}行目: Googleアカウント「${gmail}」の形式が正しくありません。`
        );
      }
      // CSV内でのGoogleアカウント重複チェック
      if (gmail) {
        if (processedGmails.includes(gmail)) {
          throw new Error(
            `${rowNum}行目: Googleアカウント「${gmail}」がCSV内で重複しています。`
          );
        }
        processedGmails.push(gmail);
      }

      // Microsoftアカウント
      const msKey = Object.keys(record).find(
        key => key === "Microsoftアカウント"
      );
      const microsoftEmail = String(record[msKey ?? ""] ?? "").trim();
      if (microsoftEmail && !validEmail(microsoftEmail)) {
        throw new Error(
          `${rowNum}行目: Microsoftアカウント「${microsoftEmail}」の形式が正しくありません。`
        );
      }
      // CSV内でのMicrosoftアカウント重複チェック
      if (microsoftEmail) {
        if (processedMicrosoftEmails.includes(microsoftEmail)) {
          throw new Error(
            `${rowNum}行目: Microsoftアカウント「${microsoftEmail}」がCSV内で重複しています。`
          );
        }
        processedMicrosoftEmails.push(microsoftEmail);
      }

      // メールアドレス
      const emailKey = Object.keys(record).find(key =>
        key.includes("メールアドレス")
      );
      const email = String(record[emailKey ?? ""] ?? "").trim();
      if (!email) {
        throw new Error(`${rowNum}行目: メールアドレスが空です。`);
      }
      if (!validEmail(email)) {
        throw new Error(
          `${rowNum}行目: メールアドレス「${email}」の形式が正しくありません。`
        );
      }
      // CSV内でのメールアドレス重複チェック
      if (processedEmails.includes(email)) {
        throw new Error(
          `${rowNum}行目: メールアドレス「${email}」がCSV内で重複しています。`
        );
      }
      processedEmails.push(email);

      // 教室名（カンマ区切り、ダブルクォート内のカンマは無視）
      const classroomKey = Object.keys(record).find(key =>
        key.includes("教室")
      );
      const classroomNamesRaw = String(record[classroomKey ?? ""] ?? "").trim();
      const parsedClassroomNames = parseClassroomNames(classroomNamesRaw);

      // 教室名のバリデーション
      if (
        !validateClassroomNames(parsedClassroomNames, validClassroomNamesList)
      ) {
        const invalidNames = parsedClassroomNames.filter(
          cn => !validClassroomNamesList.includes(cn)
        );
        throw new Error(
          `${rowNum}行目: 教室「${invalidNames.join(", ")}」は存在しません。`
        );
      }

      // オーナー関連のバリデーション
      if (currentOwnerId && id === currentOwnerId) {
        // adminの場合、オーナーのデータ自体をCSVに含めることはできない
        if (!this.isOwnerOfSchool) {
          throw new Error(
            `${rowNum}行目: ${authorityMap.owner}のデータを編集することはできません。CSVから${authorityMap.owner}の行を削除してください。`
          );
        }
        // オーナーの権限は変更不可
        if (authority !== "owner") {
          throw new Error(
            `${rowNum}行目: ${authorityMap.owner}の権限を変更することはできません。`
          );
        }
      } else {
        // 現在オーナーでない人がオーナーになろうとしていないかチェック
        if (authority === "owner") {
          throw new Error(
            `${rowNum}行目: ${authorityMap.owner}以外のスタッフを${authorityMap.owner}に変更することはできません。`
          );
        }
      }

      let passwordLoginDisabled: boolean | undefined;
      if (passwordLoginKey !== undefined) {
        const passwordLogin = readPasswordLoginCell(
          record[passwordLoginKey],
          "keep"
        );
        if (!passwordLogin.ok) {
          throw new Error(
            `${rowNum}行目: ${PASSWORD_LOGIN_DISABLED_HEADER}「${passwordLogin.raw}」は無効です。有効な値: する, しない（空欄は変更しない）`
          );
        }
        if (passwordLogin.intent.kind === "set") {
          passwordLoginDisabled = passwordLogin.intent.disabled;
        }
      }

      parsedRecords.push({
        rowNum,
        id,
        name,
        authority,
        gmail,
        microsoftEmail,
        email,
        classroomNames: parsedClassroomNames,
        ...(passwordLoginDisabled === undefined
          ? {}
          : { passwordLoginDisabled })
      });
    }

    // オーナーが複数いないかチェック
    const ownerCount = parsedRecords.filter(r => r.authority === "owner")
      .length;
    if (ownerCount > 1) {
      throw new Error(
        `${authorityMap.owner}権限を持つスタッフが複数存在します。${authorityMap.owner}は1人のみ設定できます。`
      );
    }

    return parsedRecords.map(({ rowNum, ...rest }) => rest);
  }

  async onCsvFileInputForUpdate(event: Event) {
    if (!this.school) return;
    if (!this.canBulkUpdateTutors) {
      return;
    }
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    if (!file) return;

    try {
      store.commit("START_LOADING", "CSVファイル読み込み中...");

      let text: string | ArrayBuffer | null = "";
      try {
        text = await readAsText(file);
      } catch (e) {
        console.error(e);
        throw new Error(
          `ファイルの読み込みに失敗しました。${
            e instanceof Error ? e.message : ""
          }`
        );
      }

      if (typeof text !== "string") {
        throw new Error(
          "ファイルを正常に読み込むことができませんでした。CSVファイルのデータが正しいかどうか確認してください"
        );
      }
      if (!text) {
        throw new Error("ファイル内にデータが存在しません。");
      }

      const records: { [key: string]: string }[] = parse(text, {
        columns: true,
        skipEmptyLines: true
      });

      if (records.constructor !== Array) {
        throw new Error(
          "ファイルを正常に読み込むことができませんでした。CSVファイルのデータが正しいかどうか確認してください"
        );
      }

      if (records.length === 0) {
        throw new Error("CSVファイルにデータがありません。");
      }

      // バリデーションと変換
      this.parsedStaffData = this.validateAndConvertStaffRecords(records);

      // 一括更新APIを呼び出し
      store.commit("START_LOADING", "スタッフ情報を更新中...");
      const result = await bulkUpdateTutors({
        schoolId: this.school.ref.id,
        tutors: this.parsedStaffData
      });

      // 結果を表示
      // 0件でも更新件数を表示する（ユーザーに結果を明示するため）
      const summaryParts = [];
      summaryParts.push(`${result.updatedCount}件を更新しました。`);
      if (result.skippedCount > 0) {
        summaryParts.push(
          `${result.skippedCount}件は変更がないのでスキップしました。`
        );
      }
      const summary = summaryParts.join("\n");

      if (result.errors.length > 0) {
        const errorMessages = result.errors
          .map(e => `・${e.name || e.id}: ${e.message}`)
          .join("\n");
        alert(
          `${summary}\n\n` +
            `以下のスタッフの更新でエラーが発生しました:\n${errorMessages}`
        );
      } else {
        alert(summary || "更新対象がありませんでした。");
      }

      // 画面をリロードして最新データを反映
      this.$router.go(0);
    } catch (e) {
      console.error(e);
      alert(
        `スタッフ情報の更新に失敗しました。\n${
          e instanceof Error ? e.message : "不明なエラー"
        }`
      );
    } finally {
      if (this.$refs.fileInput) {
        (this.$refs.fileInput as HTMLInputElement).value = "";
      }
      this.parsedStaffData = [];
      store.commit("END_LOADING");
    }
  }

  async handleExportAllStaffs() {
    if (!this.canBulkUpdateTutors) return;
    if (!this.school) return;
    try {
      store.commit("START_LOADING", "スタッフ情報取得中...");
      const res = await getTutorDetailsOfSchools({
        schoolId: this.school.ref.id
      });

      if (res.length === 0 || res[0].tutorDetailsWithClassrooms.length === 0) {
        alert("出力するスタッフがいません。");
        return;
      }

      const schoolData = res[0];
      const authorityMap = getAuthorityMap({ isSchool: this.isSchool });

      // adminの場合はオーナーを除外
      const tutorsToExport = this.isOwnerOfSchool
        ? [...schoolData.tutorDetailsWithClassrooms]
        : schoolData.tutorDetailsWithClassrooms.filter(
            t => t.authority !== "owner"
          );

      if (tutorsToExport.length === 0) {
        alert("出力するスタッフがいません。");
        return;
      }

      // ヘッダー行を作成
      const headerColumns = [
        "ID *編集不可",
        "名前",
        "権限",
        "Googleアカウント",
        "Microsoftアカウント",
        "メールアドレス",
        PASSWORD_LOGIN_DISABLED_HEADER,
        "教室名(カンマ区切り)"
      ];
      const headerRow = headerColumns.join(",") + "\n";

      // データ行を作成
      const dataRows = tutorsToExport.map(tutor => {
        // admin/ownerは全教室に権限があるため、教室名を空にする
        const classroomsJoined = checkIsAdminOrOwnerOfSchoolFromAuthority(
          tutor.authority
        )
          ? ""
          : // 教室名はセル内でカンマ区切りの独自形式
            // 1. カンマや"を含む教室名は内部でダブルクォートで囲む（"は""にエスケープ）
            // 2. 最後にescapeCsvFieldで全体をCSVセルとしてエスケープ
            tutor.classroomNames
              .map(name => {
                if (name.includes('"') || name.includes(",")) {
                  return `"${name.replace(/"/g, '""')}"`;
                }
                return name;
              })
              .join(",");

        const row = [
          escapeCsvField(tutor.id),
          escapeCsvField(tutor.name),
          escapeCsvField(authorityMap[tutor.authority]),
          escapeCsvField(tutor.gmail),
          escapeCsvField(tutor.microsoftEmail),
          escapeCsvField(tutor.email),
          escapeCsvField(
            formatPasswordLoginDisabledCell(
              tutor.passwordLoginDisabled ?? false
            )
          ),
          escapeCsvField(classroomsJoined)
        ];
        return row.join(",");
      });

      const csvData = headerRow + dataRows.join("\n");

      exportToUTF8Csv(
        csvData,
        `スタッフデータ_${dayjs().format("YYYY-MM-DD")}現在`
      );
    } catch (e) {
      console.error(e);
      alert(
        "スタッフ情報の取得に失敗しました。" +
          (e instanceof Error ? e.message : "")
      );
    } finally {
      store.commit("END_LOADING");
    }
  }
}
